import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { setRequestedURL } from "../util/UrlHelper";

// Auth/system routes that must never be treated as a deep-link target — doing
// so would hijack or loop the login flow. Only relevant for the web / direct-
// open fallback below; native launch URLs are always work-order deep links.
const NON_TARGET_PREFIXES = [
  "/AuthCallback",
  "/PortalAuthCallback",
  "/logout",
  "/endsession",
  "/frontchannellogout",
  "/qrcodesvc",
];

const AppUrlListener: React.FC<any> = () => {
  const history = useHistory();

  useEffect(() => {
    // Dedupe so a cold-start URL surfaced by BOTH getLaunchUrl and appUrlOpen is
    // only handled once.
    let lastHandledUrl: string | undefined;

    // Extract the in-app path from a deep-link URL, or undefined when it isn't a
    // routable target (OAuth callback, auth/system route, root, or duplicate).
    const toSlug = (url?: string): string | undefined => {
      if (!url || url === lastHandledUrl) return undefined;
      if (
        url.startsWith("com.aptean.eam://oauth/auth") ||
        url.startsWith("msauth.com.aptean.eam://auth")
      ) {
        return undefined;
      }
      let slug: string | undefined;
      try {
        const parsed = new URL(url);
        slug = `${parsed.pathname}${parsed.search}`;
      } catch {
        // Fallback for non-standard URLs: take everything after the host.
        slug = url.split(".com").pop();
      }
      if (!slug || slug === "/") return undefined;
      if (NON_TARGET_PREFIXES.some((p) => slug!.startsWith(p))) return undefined;
      lastHandledUrl = url;
      return slug;
    };

    // Persist the deep-link target so it survives the login flow; AuthCallback
    // navigates to it via getRequestedURL() after auth. Used on cold start /
    // direct web open — we deliberately DO NOT navigate here. Pushing to the WO
    // route before the user is logged in fights the auth/biometric flow and
    // loops (Bug 895833); letting the normal login flow run, then landing on the
    // WO via AuthCallback, is the correct path.
    const persistTarget = async (url?: string) => {
      const slug = toSlug(url);
      if (slug) await setRequestedURL(slug);
    };

    // Navigate to the deep link once the app is actually active. When a link is
    // tapped while the app is backgrounded, appUrlOpen can fire before the
    // WebView has resumed; pushing then leaves the route pending until the next
    // interaction (Bug 895834). Waiting for isActive lets the router render it.
    const navigateWhenActive = async (slug: string) => {
      let active = true;
      try {
        active = (await App.getState()).isActive;
      } catch {
        active = true;
      }
      if (active) {
        history.push(slug);
        return;
      }
      const stateHandle = await App.addListener("appStateChange", (state) => {
        if (state.isActive) {
          stateHandle.remove();
          history.push(slug);
        }
      });
    };

    // Warm path: the app is already running (and logged in) when a deep link is
    // opened. Persist as a safety net, then navigate.
    const routeWarm = async (url?: string) => {
      const slug = toSlug(url);
      if (!slug) return;
      await setRequestedURL(slug);
      await navigateWhenActive(slug);
    };

    // Cold start (native: getLaunchUrl) / direct open (web: window.location):
    // capture the target, but let the auth flow do the navigating.
    const initialHref = () =>
      typeof window !== "undefined" ? window.location.href : "";
    App.getLaunchUrl()
      .then((launch) => persistTarget(launch?.url || initialHref()))
      .catch(() => persistTarget(initialHref()));

    const listenerHandle = App.addListener(
      "appUrlOpen",
      (event: URLOpenListenerEvent) => routeWarm(event.url)
    );

    return () => {
      listenerHandle.then((handle) => handle.remove()).catch(() => {});
    };
  }, [history]);

  return null;
};

export default AppUrlListener;
